import { CdkDragSortEvent, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { HubConfigRolesBulkAddDrawerService } from './hub-config-roles-bulk-add-drawer/hub-config-roles-add-delete-drawer.service';
import { HubConfigRolesBulkDeleteDrawerService } from './hub-config-roles-bulk-delete-drawer/hub-config-roles-bulk-delete-drawer.service';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { DialogConfirmPromptService } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ReportingBackend } from '@libs/src/reporting/reporting.backend';
import { ToastService } from '@libs/src/toast/toast.service';
import { HubConfigRolesDrawerService } from '@main-client/src/app/hub-configuration/hub-config-roles/hub-config-roles-drawer/hub-config-roles-drawer.service';
import { HubConfigRolesSettingsService } from '@main-client/src/app/hub-configuration/hub-config-roles/hub-config-roles-settings/hub-config-roles-settings.service';

import { TranslationLookupPipe } from '@main-client/src/app/pipes/translation-lookup/translation-lookup.pipe';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Memoize } from '@libs/src/functions/memoize';
import {
  UpdateGroupRoleSuccess,
  UpdateGroupSuccess,
} from '@libs/src/groups/groups-store/groups.actions';
import { Group, GroupRole } from '@libs/src/models/group.model';
import { IAppState } from '@main-client/src/app/app.state';
import { sanitizeString } from '@main-client/src/app/hub-configuration/hub-configuration.helper';

import cloneDeep from 'lodash-es/cloneDeep';
import defer from 'lodash-es/defer';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import sortBy from 'lodash-es/sortBy';
import sum from 'lodash-es/sum';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-hub-config-roles',
  styleUrls: ['./hub-config-roles.component.scss'],
  templateUrl: './hub-config-roles.component.html',
})
export class HubConfigRolesComponent implements OnChanges {
  @Input() group: Group;
  @Input() showInviteable: boolean;
  @Input() showOptedIntoMatchmaking: boolean;
  @Input() showSearchable: boolean;
  @ViewChild('rolesContainer') rolesContainer: ElementRef;
  roleSegments: { [roleName: string]: number } = {};
  rolesClone: GroupRole[];
  enableSort = false;
  bestPractices = {
    body: $localize`:@@hubConfigRoles.bestPracticesBody:Roles should describe a group of people, not one specific individual. If you need to add more specificity to the role, consider using custom segments for additional attributes.`,
    title: $localize`:@@hubConfigRoles.bestPracticesTitle:Use specific roles or departments of your organization`,
  };
  filteredRoles: GroupRole[] = [];
  isDragDropDisabled = false;
  searchKeywordControl = this.fb.control('');
  currentPageIndex = signal(0);
  readonly PAGE_SIZE = 20;

  constructor(
    private readonly confirmDialogService: DialogConfirmPromptService,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly drawerService: HubConfigRolesDrawerService,
    private readonly fb: FormBuilder,
    private readonly groupsBackend: GroupsBackend,
    private readonly roleSettingsDrawer: HubConfigRolesSettingsService,
    private readonly reportingBackend: ReportingBackend,
    private readonly store: Store<IAppState>,
    private readonly toast: ToastService,
    private readonly translationLookupPipe: TranslationLookupPipe,
    private readonly hubConfigRolesBulkAddDrawerService: HubConfigRolesBulkAddDrawerService,
    private readonly hubConfigRolesBulkDeleteDrawerService: HubConfigRolesBulkDeleteDrawerService,
  ) {}

  ngOnChanges() {
    this.reportingBackend
      .getMemberRoles({ groupId: this.group._id })
      .pipe(filter((response) => !isEmpty(response)))
      .subscribe((response: { data?: number[][]; series?: string[] }) => {
        this.roleSegments = response.series.reduce(
          (acc, role, index) => ({
            ...acc,
            [role]: sum(response.data[index]),
          }),
          {},
        );
      });
    this.rolesClone = cloneDeep(this.group.roles);
    this.filterRolesByKeyword(this.rolesClone, this.searchKeywordControl.value);
    this.enableSort = !isEqual(
      sortBy(this.rolesClone, ['name']),
      this.rolesClone,
    );
    this.searchKeywordControl.valueChanges
      .pipe(
        startWith(this.searchKeywordControl.value),
        debounceTime(500),
        tap((value: string) => {
          this.filterRolesByKeyword(this.rolesClone, value);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  openEditDrawer(role?: GroupRole) {
    return this.drawerService.openDrawer({
      role,
      group: this.group,
      showInviteable: this.showInviteable,
      showOptedIntoMatchmaking: this.showOptedIntoMatchmaking,
      showSearchable: this.showSearchable,
    });
  }

  openDeleteDialog(role: GroupRole) {
    return this.confirmDialogService
      .openDialog({
        cancel: $localize`:@@global.cancel:Cancel`,
        color: 'warn',
        confirm: $localize`:@@hubConfigRoles.deleteDialog.cta:Delete role`,
        content: $localize`:@@hubConfigRoles.deleteDialog.description:Are you sure you want to delete "${this.translationLookupPipe.transform(
          role.name,
          ['@@groupRole'],
        )}"? You cannot undo this action.`,
        title: $localize`:@@hubConfigRoles.deleteDialog.title:Delete role`,
      })
      .afterClosed()
      .pipe(
        filter((result) => !isEmpty(result)),
        tap(() => {
          this.groupsBackend
            .deleteRoles(this.group, role)
            .pipe(
              tap((response: GroupRole[]) =>
                this.store.dispatch(
                  UpdateGroupRoleSuccess({
                    group: this.group,
                    roles: response,
                  }),
                ),
              ),
            )
            .subscribe();
        }),
      )
      .subscribe();
  }

  rolesOrderChanged(event: CdkDragSortEvent) {
    const paginationAdjustment = this.currentPageIndex() * this.PAGE_SIZE;
    const previousIndex = paginationAdjustment + event.previousIndex;
    const currentIndex = paginationAdjustment + event.currentIndex;
    if (isEqual(previousIndex, currentIndex)) {
      return;
    }
    moveItemInArray(this.rolesClone, previousIndex, currentIndex);
    this.groupsBackend
      .patchGroup(this.group, {
        roles: this.rolesClone,
      })
      .pipe(
        tap((response: Group) => {
          this.toast.showSuccess('Changes saved!');
          this.rolesClone = response.roles;
          this.store.dispatch(
            UpdateGroupSuccess({
              group: response,
            }),
          );
        }),
        catchError((error: HttpErrorResponse) => {
          this.toast.showError(error.error.message);
          this.rolesClone = this.group.roles;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  openBulkRoleAddDrawer() {
    this.hubConfigRolesBulkAddDrawerService.openDrawer();
  }

  openBulkRoleDeleteDrawer() {
    this.hubConfigRolesBulkDeleteDrawerService.openDrawer();
  }

  @Memoize()
  getEnabledInteractions(role: GroupRole) {
    const enabledInteractions = [
      role.defaults.inviteable
        ? $localize`:@@hubConfigRoles.interactions.inviteable:Messages`
        : '',
      role.defaults.opted_into_matchmaking
        ? $localize`:@@hubConfigRoles.interactions.introductions:Introductions`
        : '',
      role.defaults.searchable
        ? $localize`:@@hubConfigRoles.interactions.searchable:Search`
        : '',
    ].filter((item: string) => !isEmpty(item));
    return enabledInteractions.length ? enabledInteractions.join(', ') : 'None';
  }

  sortRolesAlphabetically(group: Group, roles: GroupRole[]) {
    this.confirmDialogService
      .openDialog({
        cancel: $localize`:@@global.cancel:Cancel`,
        color: 'primary',
        confirm: $localize`:@@global.confirm:Confirm`,
        content: $localize`:@@hubConfigRoles.sortDialog.description:Are you sure you want to sort all roles alphabetically?`,
        title: $localize`:@@hubConfigRoles.sortDialog.title:Sort roles alphabetically`,
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !isEmpty(dialogResponse)),
        concatMap(() => {
          const orderedRoles = sortBy(roles, ['name']);
          return this.groupsBackend
            .patchGroup(group, { roles: orderedRoles })
            .pipe(
              tap((response: Group) => {
                this.rolesClone = response.roles;
                this.store.dispatch(UpdateGroupSuccess({ group: response }));
                this.toast.showSuccess('Update successful!');
              }),
              catchError((response: HttpErrorResponse) => {
                this.toast.showError(getServerError(response));
                this.rolesClone = group.roles;
                return EMPTY;
              }),
            );
        }),
      )
      .subscribe();
  }

  openSettingsDrawer() {
    return this.roleSettingsDrawer.openDrawer(this.group);
  }

  filterRolesByKeyword(roles: GroupRole[], keyword: string) {
    const sanitizedKeyword = sanitizeString(keyword);
    if (!sanitizedKeyword) {
      this.filteredRoles = roles;
      this.isDragDropDisabled = false;
      return;
    }
    this.filteredRoles = roles.filter((role: GroupRole) =>
      includes(sanitizeString(role.name), sanitizedKeyword),
    );
    this.isDragDropDisabled = true;
  }

  onPageIndexChange(newPageNumber: number) {
    this.currentPageIndex.set(newPageNumber - 1);
    this.scrollToTop();
  }

  scrollToTop() {
    defer(() => this.rolesContainer.nativeElement.scrollIntoView());
  }

  dragDropPredicate() {
    return () => this.isDragDropDisabled;
  }
}
